import React, { useEffect, useRef, useState } from 'react'

import { decodeMigrationUri } from 'services/totp/googleMigration'
import imageFileToImageData from 'utils/imageDecode'
import styles from '../style.module.scss'
import own from './style.module.scss'

/**
 * Google Authenticator QR 업로드 패널 (PR7 #1514)
 * ImportUploadStep 의 googleauth 전용 본문 — 이미지 드롭/선택 + URI 붙여넣기 병행,
 * 다중 QR(batch) 누적. 스캔 즉시 디코드해 reducer(scannedQrs)에 쌓고, 정책 판정
 * (같은 장 재업로드 무시 / 다른 batch_id 거부)은 여기서 수행한다.
 * jsQR 는 동적 import(lazy 청크) — import 기능을 안 쓰면 로드되지 않는다.
 */
// isParsing: [다음] 이후 중복 검사가 [다음] 시점의 목록으로 도는 동안 추가하면 결과에서
// 조용히 빠진다 — 업로드 화면의 다른 조작과 같이 잠근다(import_template_csv D16)
const GoogleAuthUploadPanel = ({
  preset,
  scannedQrs,
  dispatch,
  t,
  isParsing = false,
  onScanningChange,
}) => {
  const fileRef = useRef()
  const [dragging, setDragging] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState(null)
  const [pasteValue, setPasteValue] = useState('')

  // 인식 중임을 부모에 알려 [다음]을 막는다 — 인식 도중 패널이 사라지면(단계 이동·닫기) 해제를 함께 알린다
  useEffect(() => {
    onScanningChange?.(scanning)
  }, [scanning, onScanningChange])
  useEffect(() => () => onScanningChange?.(false), [onScanningChange])

  // 인식(await)이 끝나기 전에 패널이 사라지면(프리셋 변경·창 닫기) 그 결과는 다른 상태 위에 쌓이거나 새 패널
  // 목록과 중복되므로 버린다 — 이를 판정하려고 true 는 effect 본문에서 켠다
  const isMountedRef = useRef(false)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const totalAccounts = scannedQrs.reduce((sum, q) => sum + q.accountCount, 0)
  const batchSize = scannedQrs.find((q) => q.batch)?.batch?.size || null
  const remaining = batchSize != null ? batchSize - scannedQrs.length : null

  // 스캔된 URI 를 정책 검사 후 누적. 실패 시 에러 코드만 상태로(secret/URI 는 로그 금지).
  const addMigrationUri = (uri) => {
    // 같은 QR 재업로드는 batch 필드 유무와 무관하게 URI 자체로 판정(리뷰 A3 —
    // batch 없는 단일 QR 이중 추가 방지)
    if (scannedQrs.some((q) => q.uri === uri)) {
      setError('alreadyAdded')
      return
    }

    let decoded
    try {
      decoded = decodeMigrationUri(uri)
    } catch (e) {
      setError(e.code === 'NOT_MIGRATION_URI' ? 'notMigrationQr' : 'invalidPayload')
      return
    }

    const { batch } = decoded
    if (batch && scannedQrs.length > 0) {
      const existing = scannedQrs.find((q) => q.batch)
      // batch_id 는 양쪽에 실제 값이 있을 때만 비교(리뷰 A6 — 생략 시 null 보존)
      if (
        existing &&
        existing.batch.id != null &&
        batch.id != null &&
        existing.batch.id !== batch.id
      ) {
        setError('differentBatch') // 다른 내보내기의 QR — 현재 누적 유지, 이 장 거부
        return
      }
      if (scannedQrs.some((q) => q.batch && q.batch.index === batch.index)) {
        setError('alreadyAdded') // 같은 장 재업로드 — 무시
        return
      }
    }

    const accountCount = decoded.accounts.filter((a) => !a.skipped).length
    setError(null)
    dispatch({ type: 'ADD_SCANNED_QR', payload: { uri, batch, accountCount } })
  }

  const handleImageFile = async (file) => {
    // 스캔 진행 중 재진입 차단(리뷰 A1) — await 사이에 추가된 항목을 못 보는
    // stale closure 로 정책 검사가 우회되는 것을 막는다
    if (!file || scanning || isParsing) return
    setScanning(true)
    setError(null)
    try {
      const imageData = await imageFileToImageData(file)
      const { default: jsQR } = await import('jsqr')
      if (!isMountedRef.current) return
      const result = jsQR(imageData.data, imageData.width, imageData.height)
      if (!result || !result.data) {
        setError('noQrFound')
        return
      }
      addMigrationUri(result.data)
    } catch (e) {
      // 이미지 디코딩 자체 실패(손상 파일 등) — 사유 코드만 남긴다
      setError('noQrFound')
    } finally {
      setScanning(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handlePaste = () => {
    const value = pasteValue.trim()
    if (!value || scanning || isParsing) return // 스캔 중 추가 금지(리뷰 A1 — stale closure 방지)
    addMigrationUri(value)
    setPasteValue('')
  }

  const accepted = preset.acceptedExts.join(',')

  return (
    <div>
      <div
        className={[styles.dropZone, dragging ? styles.dropZoneActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          handleImageFile(e.dataTransfer.files[0])
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') fileRef.current?.click()
        }}
      >
        {scanning && scannedQrs.length === 0 && <p>{t('record.totp.import.scanning')}</p>}
        {!scanning && scannedQrs.length === 0 && <p>{t('record.totp.import.dropHint')}</p>}
        {/* 인식 결과를 드롭존 안에 표시 — 아래쪽 별도 블록은 모달 스크롤 밖에 잘려
            "업로드했는데 무반응"으로 보였다(2026-08-06 QA). 누적 요약은 다음 장 스캔
            중에도 유지한다(PR #1770 리뷰 — 배치 업로드 중 정보가 사라지지 않게). */}
        {scannedQrs.length > 0 && (
          <>
            <p className={own.googleAuthDropStatus}>
              {t('record.totp.import.scanProgress', {
                qrCount: scannedQrs.length,
                accountCount: totalAccounts,
              })}
            </p>
            {(scanning || (remaining != null && remaining > 0)) && (
              <p className={own.googleAuthDropSub}>
                {scanning
                  ? t('record.totp.import.scanning')
                  : t('record.totp.import.needMoreQr', { count: remaining })}
              </p>
            )}
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={accepted}
          style={{ display: 'none' }}
          onChange={(e) => handleImageFile(e.target.files[0])}
        />
      </div>

      {error != null && (
        <p className={own.googleAuthError}>{t(`record.totp.import.error.${error}`)}</p>
      )}

      <div className={own.googleAuthPaste}>
        <input
          type="text"
          value={pasteValue}
          placeholder={t('record.totp.import.pasteHint')}
          onChange={(e) => setPasteValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handlePaste()
          }}
        />
        <button type="button" onClick={handlePaste} disabled={!pasteValue.trim() || isParsing}>
          {t('record.totp.import.pasteAdd')}
        </button>
      </div>
    </div>
  )
}

export default GoogleAuthUploadPanel
